import { useCallback, useEffect, useRef, useState } from "react";
import { TableInstance, TableState } from "react-table";
import { runtimeEnv } from "configuration";
import { sandboxApi } from "services/sandbox-api/api";
import { sandboxClient } from "services/sandbox-api/sandboxClient";
import { SandboxApi } from "services/sandbox-api/types";
import { useAppSelector } from "store";
import { selectCurrentUser } from "store/features";
import { CodeShareMetadata } from "types";
import { Alert, Button, LabeledInput, LabeledSelect, Table } from "@itwin/itwinui-react";
import styles from "./SandboxImportView.module.css";

const environments = [
  {
    label: "Dev",
    value: "https://dev-connect-itwinjscodesandbox.bentley.com",
    url: "https://dev.imodeljs.org"
  },
  {
    label: "QA",
    value: "https://qa-connect-itwinjscodesandbox.bentley.com",
    url: "https://qa.imodeljs.org"
  },
  {
    label: "Prod",
    value: "https://connect-itwinjscodesandbox.bentley.com",
    url: "https://itwinjs.org"
  },
]

type TableData = CodeShareMetadata & Record<string, unknown>;
type Overwrite = "checking" | "no conflict" | "overwrite";

interface Sandbox extends TableData {
  overwrite?: Overwrite;
};


//b44ec7df-cd25-4a10-ba11-bafe02e91c35

const getEnvironments = () => {
  if (window.location.origin.startsWith("http://localhost"))
    return environments.slice(1);
  else
    return environments.filter((item) => item.url !== window.location.origin);
}

const columns = [{
  id: 'id',
  Header: 'Id',
  accessor: 'id'
}, {
  id: 'name',
  Header: 'Name',
  accessor: 'name'
}, {
  id: 'description',
  Header: 'Description',
  accessor: 'description',
}, {
  id: 'username',
  Header: 'User Name',
  accessor: 'username',
  width: "180px"
}, {
  id: 'overwrite',
  Header: 'Status',
  accessor: 'overwrite',
  width: "100px"
}];

export const SandboxImportView = () => {

  const user = useAppSelector(selectCurrentUser);
  const sandboxUser = useAppSelector((state) => state.auth.sandboxUser);

  const [userId, setUserId] = useState<string | undefined>(user?.id);
  const [backendUrl, setBackednUrl] = useState<string>(getEnvironments()[0].value);
  const [selectedSandboxes, setSelectedSandboxes] = useState<Sandbox[]>([]);
  const [sandboxes, setSandboxes] = useState<Sandbox[]>([])
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const tableInstance = useRef<TableInstance<Sandbox> | undefined>();
  const [selectedRowIds, setSelectedRowIds] = useState<Record<string, boolean>>({})

  const [updateSandbox] = sandboxApi.useCodeShareUpdateMutation();
  const [updateSandboxFiles] = sandboxApi.useCodeShareUpdateFilesMutation();
  const [createSandbox] = sandboxApi.useCodeShareCreateMutation();

  const [importLog, setImportLog] = useState<string[]>([]);

  useEffect(() => {
    setUserId(user?.id)
  }, [user])

  const onSearch = () => {
    if (!userId)
      return [];

    setIsLoading(true)
    setError("");
    fetch(`${backendUrl}/api/user/${userId}/codeshares?orderBy=name`)
      .then(async (response: any) => {
        const isJson = response.headers.get("content-type")?.includes("application/json") || response.headers.get("content-type")?.includes("application/problem+json");
        const data = isJson ? await response.json() : undefined;

        if (!response.ok) {

          if (data !== undefined)
            return Promise.reject(JSON.stringify(data));
          else
            return Promise.reject(`Error occured reading sandboxes. ${response.status} ${response.statusText}`);
        }

        setSandboxes(data as Sandbox[]);
      })
      .catch((data) => {
        setError(data);
      })
      .finally(() => setIsLoading(false));
  }

  const onSelect = useCallback((rows?: Sandbox[]) => {
    setSelectedSandboxes(rows ?? []);
  }, []);

  const onCheck = () => {
    if (!sandboxUser) {
      setError("Yuo must be signed in and created sandbox user")
      return;
    }

    const results: Promise<boolean>[] = [];
    for (const sandbox of selectedSandboxes) {
      results.push(sandboxClient.sandboxNameExists({ userName: sandboxUser.username, sandboxName: sandbox.name }))
    }

    Promise.all(results).then(response => {
      sandboxes.forEach((sandbox) => sandbox.overwrite = undefined);

      for (let i = 0; i < response.length; i++) {
        const _sandbox = sandboxes.find((item) => item.id === selectedSandboxes[i].id);
        _sandbox!.overwrite = response[i] ? "overwrite" : "no conflict";
      }

      setSandboxes([...sandboxes]);

      for (const rowId of Object.keys(selectedRowIds))
        tableInstance.current?.toggleRowSelected(rowId, selectedRowIds[rowId]);
    })
  };

  const getSandboxView = (userId: string, sandboxId: string) => {
    return fetch(`${backendUrl}/api/user/${userId}/codeshare/${sandboxId}/view`)
      .then((response) => response.json())
      .then((data) => data as SandboxApi.CodeShareDataResponse);
  }
  const resolveSandboxView = (sandboxName: string) => {
    return fetch(`${runtimeEnv.REACT_APP_SANDBOX_BACKEND_URL}/api/username/${sandboxUser?.username}/codeshare/${sandboxName}/view`)
      .then((response) => response.json())
      .then((data) => data as SandboxApi.CodeShareDataResponse);
  }

  const onImport = async () => {
    if (!user) {
      setError("Sign-in required");
      return;
    }

    setImportLog([]);
    for (const sandbox of selectedSandboxes) {
      setImportLog((state) => { state.push(`Importing sandbox "${sandbox.name}"... `); return [...state]; })
      const sandboxData = await getSandboxView(sandbox.userId, sandbox.id);
      if (sandbox.overwrite === "no conflict") {
        await createSandbox({
          name: sandbox.name,
          description: sandbox.description,
          attributes: sandbox.attributes,
          files: sandboxData.files,
          optionalFiles: sandboxData.optionalFiles,
          iModels: sandboxData.iModels,
          modules: sandboxData.modules,
        }).unwrap();
        setImportLog((state) => { state.push("   Done. Created"); return [...state]; })
      } else {
        const resolvedSandboxData = await resolveSandboxView(sandbox.name);
        await updateSandbox({
          id: resolvedSandboxData.id,
          userId: user.id,
          name: resolvedSandboxData.name,
          description: sandbox.description,
          attributes: sandbox.attributes,
          optionalFiles: sandboxData.optionalFiles,
          iModels: sandboxData.iModels,
          modules: sandboxData.modules,
        }).unwrap();
        await updateSandboxFiles({
          id: resolvedSandboxData.id,
          userId: user.id,
          files: sandboxData.files,
        }).unwrap();
        setImportLog((state) => { state.push("   Done. Overwritten"); return [...state]; })
      }
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <div className={styles.picker}>
          <LabeledSelect label="Environment:" options={getEnvironments() ?? []} value={backendUrl} onChange={setBackednUrl} />
          <LabeledInput label="IMS User Id:" value={userId} onChange={(event) => setUserId(event.target.value)} className={styles.userId} />
          <Button styleType="high-visibility" onClick={onSearch} disabled={!userId}>Search Sandboxes</Button>
          <Button styleType="high-visibility" onClick={onCheck} disabled={selectedSandboxes.length === 0}>Check</Button>
          <Alert type="warning" className={styles.warning}>Import action overwrites existing sandboxes! It's not reversible!</Alert>
        </div>
        {error
          &&
          <Alert type="negative" className={styles.error}>{error}</Alert>}

        <div>
          <Table<Sandbox>
            className={styles.resultsTable}
            columns={columns}
            data={sandboxes}
            stateReducer={useCallback((newState: TableState<Sandbox>, action: any, _prevState: TableState<Sandbox>, instance?: TableInstance<Sandbox>) => {
              tableInstance.current = instance;
              if (action.type === "toggleRowSelected" && instance) {
                setSelectedRowIds(newState.selectedRowIds)
              }
              return newState;
            }, [])}
            emptyTableContent='No data.'
            isSelectable={true}
            styleType="zebra-rows"
            columnResizeMode="fit"
            density="extra-condensed"
            isLoading={isLoading}
            onSelect={onSelect}
            selectionMode="multi" />

          <div className={styles.importBtn}>
            <Button styleType="high-visibility" onClick={onImport} disabled={!selectedSandboxes.length || selectedSandboxes.some((item) => item.overwrite === undefined)}>Import</Button>
          </div>

          {importLog.length > 0 && <pre className={[styles.log, "iui-code"].join(" ")}>{importLog.join("\n")}</pre>}
        </div>
      </div>
    </div>
  )
}